import {useQuery, QueryKey, Query} from '@tanstack/react-query';
import {CommonService} from "../services";
import {useCallback, useEffect, useState} from "react";

type HttpMethod = 'get' | 'post' | 'put' | 'delete';

interface FetchDataOptions {
    staleTime?: number;
    cacheTime?: number;
    initialApiCall?: boolean;
    refetchOnMount?: boolean | "always" | ((query: Query<any, Error, any, QueryKey>) => (boolean | "always")) | undefined;
    retry?:number;
    refetchInterval?: number | false | ((query: Query<any, Error, any, QueryKey>) => (number | false | undefined)) | undefined,
}

interface IFetchData {
    url: string;
    method?: HttpMethod;
    payload?: any;
    headers?: HeadersInit;
    queryKey: QueryKey;
    options?: FetchDataOptions;
}

const useReactQuery = (config: IFetchData) => {
    const {
        url,
        method = 'get',
        payload,
        headers,
        queryKey,
        options
    } = config;
    const {
        staleTime = 1000 * 60 * 1, // 1 minute;
        cacheTime = 1000 * 60 * 1, // 1 minute
        initialApiCall = true,
        refetchOnMount = "always" as const,
    } = options || {};
    const fetchData = useCallback(async () => {
        const apiCall = {
            get: CommonService._api.get,
            post: CommonService._api.post,
            put: CommonService._api.put,
            delete: CommonService._api.delete
        }[method];

        if (!apiCall) {
            throw new Error(`Invalid HTTP method: ${method}`);
        }

        try {
            const response= await apiCall(url, payload, headers) as any;
            return response?.data ?? [];
        } catch (error: any) {
            throw new Error(`Error fetching data: ${error?.message}`);
        }
    }, [method, url, payload, headers]);

    return useQuery({
        queryKey: queryKey,
        queryFn: fetchData,
        staleTime: staleTime,
        gcTime: cacheTime,
        enabled: initialApiCall,
        refetchOnMount: refetchOnMount,
        retry: 3,
        retryDelay: 1000,
        ...options
    });
};

export default useReactQuery;

export function useDebounce(value:QueryKey, delay:any=1000) {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = useState<QueryKey>(value);

    useEffect(
        () => {
            // Update debounced value after delay
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);

            // Cancel the timeout if value changes (also on delay change or unmount)
            // This is how we prevent debounced value from updating if value is changed ...
            // .. within the delay period. Timeout gets cleared and restarted.
            return () => {
                clearTimeout(handler);
            };
        },
        [value, delay] // Only re-call effect if value or delay changes
    );

    return debouncedValue;
}

/*
How to use
import {useReactQuery} from '../hooks/useReactQuery';
const {data:overSpeedData,isLoading:isOverSpeedLoading,dataUpdatedAt}=useReactQuery({
        url: APIConfig.GET_SAFETY_OVERSPEED_DATA.URL,
        method: APIConfig.GET_SAFETY_OVERSPEED_DATA.METHOD,
        payload: {
            city_code: city,
            date
        },
        options:{
            refetchOnMount:true,
            initialApiCall:false,
        },
        queryKey:[city,date]
    })
 */
